import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../api';
import type { ImportItem, ImportLimits } from '../../types';
import { formatBytes } from '../../lib/format';
import { ActionBar, StepHeader, primaryBtn, secondaryBtn } from './ui';

const SUGGESTED = 10;
const MAX_FILES = 40;
const FILES_PER_REQUEST = 10;
const POLL_MS = 1200;
/** Processing finished for these; ORGANIZING means only the rebuild is left. */
const PROCESSED = new Set(['ORGANIZING', 'COMPLETED', 'DUPLICATE', 'FAILED']);

interface Picked {
  key: string;
  file: File;
  preview: string;
  problem: string | null;
}

function extOf(name: string) {
  const i = name.lastIndexOf('.');
  return i === -1 ? '' : name.slice(i).toLowerCase();
}

/**
 * Steps 1–2 of "Make an episode": pick a handful of photos and videos, upload
 * them as one batch, wait while dates, places, enhanced copies and
 * fingerprints are made, then turn them into one episode.
 */
export default function PickStep({ onCreated }: { onCreated: (episodeId: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [limits, setLimits] = useState<ImportLimits | null>(null);
  const [picked, setPicked] = useState<Picked[]>([]);
  const [phase, setPhase] = useState<'pick' | 'upload' | 'process' | 'compose' | 'error'>('pick');
  const [sentPct, setSentPct] = useState(0);
  const [items, setItems] = useState<ImportItem[]>([]);
  /** Which import item each picked file became. */
  const [itemIdByKey, setItemIdByKey] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.importLimits().then(setLimits).catch(() => setLimits(null));
  }, []);
  // Free the preview images when leaving.
  const pickedRef = useRef(picked);
  pickedRef.current = picked;
  useEffect(() => () => pickedRef.current.forEach((p) => URL.revokeObjectURL(p.preview)), []);

  const add = (list: FileList | null) => {
    if (!list) return;
    // Copy now: clearing the input below empties the live FileList.
    const files = Array.from(list);
    setPicked((prev) => {
      const seen = new Set(prev.map((p) => p.key));
      const next = [...prev];
      for (const file of files) {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (seen.has(key) || next.length >= MAX_FILES) continue;
        seen.add(key);
        let problem: string | null = null;
        if (file.size === 0) problem = 'Empty file';
        else if (limits && !limits.extensions.includes(extOf(file.name))) problem = 'Not a supported format';
        else if (limits && file.size > limits.maxFileBytes) problem = `Over ${formatBytes(limits.maxFileBytes)}`;
        next.push({ key, file, preview: URL.createObjectURL(file), problem });
      }
      return next;
    });
    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = (key: string) =>
    setPicked((prev) => {
      const gone = prev.find((p) => p.key === key);
      if (gone) URL.revokeObjectURL(gone.preview);
      return prev.filter((p) => p.key !== key);
    });

  const valid = picked.filter((p) => !p.problem);

  const start = async () => {
    setError(null);
    setPhase('upload');
    const batchId = crypto.randomUUID();
    const total = valid.reduce((n, p) => n + p.file.size, 0) || 1;
    let done = 0;
    try {
      for (let i = 0; i < valid.length; i += FILES_PER_REQUEST) {
        const slice = valid.slice(i, i + FILES_PER_REQUEST);
        const chunk = slice.map((p) => p.file);
        const res = await api.upload(chunk, {
          batchId,
          onProgress: (loaded) => setSentPct(Math.min(100, Math.round(((done + loaded) / total) * 100))),
        });
        setItemIdByKey((m) => {
          const next = new Map(m);
          res.items.forEach((r, j) => r.importItemId && next.set(slice[j].key, r.importItemId));
          return next;
        });
        done += chunk.reduce((n, f) => n + f.size, 0);
      }
    } catch (err) {
      setError(`Upload stopped: ${(err as Error).message}`);
      setPhase('error');
      return;
    }

    // Wait until every file has been read and prepared.
    setPhase('process');
    let list: ImportItem[] = [];
    for (;;) {
      try {
        list = (await api.imports({ batchId, limit: 200 })).items;
        setItems(list);
      } catch {
        /* keep polling through a brief server hiccup */
      }
      if (list.length > 0 && list.every((i) => PROCESSED.has(i.status))) break;
      await new Promise((r) => setTimeout(r, POLL_MS));
    }

    const mediaIds = [...new Set(list.filter((i) => i.status !== 'FAILED' && i.media).map((i) => i.media!.id))];
    if (mediaIds.length === 0) {
      setError('None of these files could be read, so there is nothing to make an episode from.');
      setPhase('error');
      return;
    }

    setPhase('compose');
    for (let attempt = 0; ; attempt++) {
      try {
        const ep = await api.composeEpisode(mediaIds);
        onCreated(ep.id);
        return;
      } catch (err) {
        // 409: a duplicate that was already in the library is still finishing.
        if (err instanceof ApiError && err.status === 409 && attempt < 20) {
          await new Promise((r) => setTimeout(r, POLL_MS));
          continue;
        }
        setError((err as Error).message);
        setPhase('error');
        return;
      }
    }
  };

  if (phase !== 'pick') {
    const ready = items.filter((i) => PROCESSED.has(i.status) && i.status !== 'FAILED').length;
    const failed = items.filter((i) => i.status === 'FAILED');
    return (
      <div>
        <StepHeader
          step={2}
          title={phase === 'compose' ? 'Putting your episode together…' : 'Getting your memories ready'}
          subtitle="Reading when and where each one was taken, and preparing the enhanced copies. A few seconds per photo."
        />
        <div role="status" aria-live="polite" className="rounded-2xl bg-ink-900 p-5 ring-1 ring-white/5">
          {phase === 'upload' && (
            <>
              <div className="mb-1.5 flex justify-between text-sm text-white/70">
                <span>Uploading {valid.length} {valid.length === 1 ? 'file' : 'files'}</span>
                <span>{sentPct}%</span>
              </div>
              <Bar pct={sentPct} />
            </>
          )}
          {(phase === 'process' || phase === 'compose') && (
            <>
              <div className="mb-1.5 flex justify-between text-sm text-white/70">
                <span>{phase === 'compose' ? 'Almost done' : `${ready} of ${valid.length} ready`}</span>
              </div>
              <Bar pct={phase === 'compose' ? 100 : Math.round((ready / Math.max(1, valid.length)) * 100)} />
            </>
          )}
          <ul className="mt-5 grid grid-cols-5 gap-2 sm:grid-cols-8">
            {valid.map((p) => {
              const item = items.find((i) => i.id === itemIdByKey.get(p.key));
              const done = item && PROCESSED.has(item.status) && item.status !== 'FAILED';
              return (
                <li key={p.key} className="relative aspect-square overflow-hidden rounded-md bg-white/5">
                  <Thumb picked={p} className={`h-full w-full object-cover transition ${done ? '' : 'opacity-40'}`} />
                  {item?.status === 'FAILED' && (
                    <span className="absolute inset-0 grid place-items-center bg-black/60 text-xs text-red-200">Failed</span>
                  )}
                  {done && (
                    <span className="absolute right-1 bottom-1 grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-[11px] text-black">
                      ✓
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          {failed.length > 0 && (
            <p className="mt-4 text-xs text-amber-200/80">
              {failed.length} {failed.length === 1 ? 'file' : 'files'} couldn’t be read and will be left out:{' '}
              {failed.map((f) => f.filename).join(', ')}
            </p>
          )}
          {phase === 'error' && error && (
            <div className="mt-4" role="alert">
              <p className="text-sm text-red-200">{error}</p>
              <button onClick={() => setPhase('pick')} className={`${secondaryBtn} mt-3`}>
                Back to your picks
              </button>
            </div>
          )}
        </div>
        <p className="mt-3 text-xs text-white/35">Keep this tab open until it finishes.</p>
      </div>
    );
  }

  return (
    <div>
      <StepHeader
        step={1}
        title="Make an episode"
        subtitle={`Pick the photos and videos from one occasion. About ${SUGGESTED} works best.`}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          add(e.dataTransfer.files);
        }}
        className="focus-ring flex w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/15 px-6 py-10 text-center transition hover:border-white/30"
      >
        <span className="text-base font-semibold">{picked.length ? 'Add more' : 'Choose photos & videos'}</span>
        <span className="mt-1 text-sm text-white/45">From your phone’s gallery or this computer</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        className="sr-only"
        tabIndex={-1}
        aria-label="Choose photos and videos"
        onChange={(e) => add(e.target.files)}
      />

      {picked.length > 0 && (
        <>
          <p className="mt-5 mb-2 text-sm text-white/65">
            {valid.length} picked
            {valid.length < 3 && ' · a few more makes a better episode'}
            {valid.length > 20 && ' · that’s a lot for one episode, but it works'}
            {picked.length >= MAX_FILES && ` · ${MAX_FILES} is the most for one episode`}
          </p>
          <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {picked.map((p) => (
              <li key={p.key} className="relative aspect-square overflow-hidden rounded-lg bg-white/5">
                <Thumb picked={p} className={`h-full w-full object-cover ${p.problem ? 'opacity-30' : ''}`} />
                {p.problem && (
                  <span className="absolute inset-x-0 bottom-0 bg-black/70 px-1.5 py-1 text-[11px] text-amber-200">
                    {p.problem}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => remove(p.key)}
                  aria-label={`Remove ${p.file.name}`}
                  className="focus-ring absolute top-1 right-1 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-sm text-white/85 hover:bg-black/80"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <ActionBar>
        <button onClick={start} disabled={valid.length === 0} className={primaryBtn}>
          Make an episode{valid.length > 0 ? ` from ${valid.length}` : ''}
        </button>
      </ActionBar>
    </div>
  );
}

function Bar({ pct }: { pct: number }) {
  return (
    <div className="h-1 overflow-hidden rounded-full bg-white/10">
      <div className="h-full bg-accent transition-[width] duration-300" style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Local preview before anything is uploaded. HEIC may not preview in some browsers. */
function Thumb({ picked, className }: { picked: Picked; className: string }) {
  const [broken, setBroken] = useState(false);
  if (picked.file.type.startsWith('video/')) {
    return <video src={picked.preview} muted preload="metadata" playsInline className={className} />;
  }
  if (broken) {
    return (
      <span className={`${className} grid place-items-center p-1 text-center text-[10px] break-all text-white/50`}>
        {picked.file.name}
      </span>
    );
  }
  return <img src={picked.preview} alt="" className={className} onError={() => setBroken(true)} />;
}
